/**
 * Glocke mit offenen Aufgaben – rote Zahl, sobald etwas zu tun ist.
 *
 * Gezählt wird nur, was die Person selbst erledigen muss (Freigaben, für die
 * Verwaltung gescheiterte Tickets); was das ist, entscheidet der Server.
 * Abgefragt wird alle zwei Minuten und beim Zurückkehren ins Fenster – nicht
 * im Hintergrund-Tab, dort sieht die Zahl ohnehin niemand.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Body1,
  Button,
  Caption1,
  CounterBadge,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { AlertRegular, ChevronRightRegular } from '@fluentui/react-icons';
import { useSession } from '../lib/session.js';
import { useI18n } from '../i18n/index.js';
import { api } from '../api/endpoints.js';
import type { TodoSummary } from '../api/types.js';

/** Abstand der Abfragen. */
export const TODO_POLL_MS = 120_000;

const useStyles = makeStyles({
  wrap: { position: 'relative', display: 'inline-flex' },
  badge: { position: 'absolute', insetBlockStart: '-2px', insetInlineEnd: '-2px', pointerEvents: 'none' },
  surface: { minWidth: '16rem', maxWidth: '22rem', display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS },
  item: { justifyContent: 'space-between', width: '100%', textAlign: 'start' },
  muted: { color: tokens.colorNeutralForeground3 },
});

export function NotificationBell() {
  const styles = useStyles();
  const { client } = useSession();
  const { t, plural } = useI18n();
  const navigate = useNavigate();
  const [todos, setTodos] = useState<TodoSummary>({ total: 0, items: [] });
  const [open, setOpen] = useState(false);
  const laufend = useRef<AbortController | null>(null);

  const laden = useCallback(() => {
    laufend.current?.abort();
    const controller = new AbortController();
    laufend.current = controller;
    api
      .myTodos(client, controller.signal)
      .then(setTodos)
      // Eine ausgefallene Abfrage ist kein Grund für eine Fehlermeldung:
      // Die Glocke bleibt beim letzten Stand, die nächste Runde kommt.
      .catch(() => undefined);
  }, [client]);

  useEffect(() => {
    laden();
    const timer = window.setInterval(() => {
      if (document.visibilityState === 'visible') laden();
    }, TODO_POLL_MS);
    const sichtbar = () => {
      if (document.visibilityState === 'visible') laden();
    };
    document.addEventListener('visibilitychange', sichtbar);
    window.addEventListener('focus', sichtbar);
    return () => {
      window.clearInterval(timer);
      document.removeEventListener('visibilitychange', sichtbar);
      window.removeEventListener('focus', sichtbar);
      laufend.current?.abort();
    };
  }, [laden]);

  const beschriftung =
    todos.total > 0 ? t('todos.labelCount', { count: todos.total }) : t('todos.label');

  return (
    <Popover open={open} onOpenChange={(_, d) => {
      setOpen(d.open);
      // Beim Öffnen frisch: Wer nachsieht, will den aktuellen Stand.
      if (d.open) laden();
    }} positioning="below-end">
      <PopoverTrigger disableButtonEnhancement>
        <span className={styles.wrap}>
          <Button appearance="subtle" icon={<AlertRegular />} aria-label={beschriftung} title={beschriftung} />
          {todos.total > 0 ? (
            <CounterBadge className={styles.badge} count={todos.total} color="danger" size="small" overflowCount={99} />
          ) : null}
        </span>
      </PopoverTrigger>
      <PopoverSurface className={styles.surface} aria-label={t('todos.label')}>
        <Body1>
          <strong>{t('todos.label')}</strong>
        </Body1>
        {todos.items.length === 0 ? (
          <Caption1 className={styles.muted}>{t('todos.none')}</Caption1>
        ) : (
          todos.items.map((item) => (
            <Button
              key={item.kind}
              appearance="subtle"
              className={styles.item}
              iconPosition="after"
              icon={<ChevronRightRegular />}
              onClick={() => {
                setOpen(false);
                navigate(item.path);
              }}
            >
              {plural(`todos.${item.kind}.one` as const, `todos.${item.kind}.other` as const, item.count)}
            </Button>
          ))
        )}
      </PopoverSurface>
    </Popover>
  );
}
